import {formatDate, formatEuro, groupCostsBySupplier, safeDocumentUrl} from '../../core/billing/billing.core.js';
import {escapeHtml} from '../../core/escape-html.js';

const NONE = '-';

/**
 * @param {string|null} documentUrl
 * @returns {string} a link opening in a new tab for an https URL, else the placeholder; the URL is
 *   escaped and only ever placed in the quoted href
 */
function documentCell(documentUrl) {
    const url = safeDocumentUrl(documentUrl);
    return url === null
        ? NONE
        : `<a class="document-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`
            + 'Open <span class="visually-hidden">document (opens in a new tab)</span></a>';
}

/**
 * @param {import('../../shell/types/billing.types.js').PlatformCostApiResponse} cost
 * @returns {string} one table row
 */
function costRow(cost) {
    const label = escapeHtml(cost.label);
    return `
        <tr>
            <td class="cost-label">${label}</td>
            <td class="nowrap">${escapeHtml(cost.invoiceNumber || NONE)}</td>
            <td class="nowrap">${escapeHtml(formatDate(cost.periodFrom))} to ${escapeHtml(formatDate(cost.periodTo))}</td>
            <td class="num">${escapeHtml(formatEuro(cost.amount) || NONE)}</td>
            <td class="nowrap">${documentCell(cost.documentUrl)}</td>
            <td class="actions">
                <button type="button" class="btn btn-outline btn-sm btn-cost-edit" data-id="${cost.id}" aria-label="Edit platform cost ${label}">
                    <i aria-hidden="true" class="fa-solid fa-pen-to-square"></i> Edit
                </button>
                <button type="button" class="btn btn-delete btn-sm btn-cost-delete" data-id="${cost.id}" aria-label="Delete platform cost ${label}">
                    <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                </button>
            </td>
        </tr>`;
}

/**
 * @param {{supplier: string, costs: import('../../shell/types/billing.types.js').PlatformCostApiResponse[], total: number}} group
 * @returns {string} one supplier: a header row with its name, row count and subtotal, then its rows
 */
function supplierGroup(group) {
    const count = `${group.costs.length} ${group.costs.length === 1 ? 'row' : 'rows'}`;
    return `
        <tbody class="supplier-group">
            <tr class="supplier-row">
                <th scope="rowgroup" colspan="6"><div class="supplier-head">${escapeHtml(group.supplier)} <span class="supplier-count">${count}</span> <span class="supplier-total">Subtotal ${escapeHtml(formatEuro(group.total))}</span></div></th>
            </tr>
            ${group.costs.map(costRow).join('')}
        </tbody>`;
}

/**
 * @param {HTMLElement} container
 * @param {import('../../shell/types/billing.types.js').PlatformCostApiResponse[]} costs any order; grouped and sorted here
 * @param {boolean} showingAll whether the list covers all time rather than the last 12 months
 */
export function renderPlatformCosts(container, costs, showingAll) {
    if (costs.length === 0) {
        container.innerHTML = `<p class="cost-empty">${showingAll
            ? 'No platform costs yet.'
            : 'No platform costs in the last 12 months. Choose All to see older ones.'}</p>`;
        return;
    }
    container.innerHTML = `
        <div class="table-wrap">
            <table class="cost-table">
                <thead>
                    <tr>
                        <th scope="col">Label</th>
                        <th scope="col">Invoice</th>
                        <th scope="col">Period</th>
                        <th scope="col" class="num">Amount</th>
                        <th scope="col">Document</th>
                        <th scope="col" class="actions"><span class="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                ${groupCostsBySupplier(costs).map(supplierGroup).join('')}
            </table>
        </div>`;
}
